import React, { useState, useEffect, useRef } from 'react';
import { Download, AlertCircle, Loader2, RefreshCw, X, Clock, User, Tag, FileText, CheckCircle, XCircle } from 'lucide-react';
import './Auditlogs.css';
import Navbar from '../Navbar/Navbar';
import { getBackendUrl } from '../../../api';
import TopBar from '../Navbar/Mainbar.jsx';
import Swal from 'sweetalert2';
const ROWS_PER_PAGE = 10;

/* ─── helpers ─────────────────────────────────────────────── */
const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString('en-US', {
      year:'numeric', month:'2-digit', day:'2-digit',
      hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false
    });
  } catch { return String(ts); }
};

/* ─── Log Detail Modal ────────────────────────────────────── */
const LogDetailModal = ({ log, onClose }) => {
  const backdropRef = useRef(null);
  if (!log) return null;
  const isError = log.activity_type === 'error';

  return (
    <div
      ref={backdropRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        backdropFilter: 'blur(3px)',
        animation: 'auditFadeIn 0.15s ease'
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '500px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden',
        animation: 'auditSlideUp 0.18s ease'
      }}>
        <div style={{
          background: isError ? '#fef2f2' : '#f0fdf4',
          borderBottom: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
          padding: '18px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '50%',
              background: isError ? '#ef4444' : '#22c55e',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {isError ? <XCircle size={18} color="#fff" /> : <CheckCircle size={18} color="#fff" />}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{log.type}</div>
              <div style={{ fontSize: '12px', color: isError ? '#ef4444' : '#16a34a', fontWeight: 600, marginTop: '1px' }}>
                {isError ? '✕ Error' : '✓ Success'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'rgba(0,0,0,0.06)', cursor: 'pointer',
            width: '30px', height: '30px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280'
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '22px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ width:'34px', height:'34px', borderRadius:'9px', background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Clock size={15} color="#6b7280" />
              </div>
              <div>
                <div style={{ fontSize:'11px', color:'#9ca3af', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'3px' }}>Timestamp</div>
                <div style={{ fontSize:'13.5px', color:'#111827', fontWeight:500 }}>{formatTimestamp(log.created_at || log.timestamp)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ width:'34px', height:'34px', borderRadius:'9px', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <User size={15} color="#3b82f6" />
              </div>
              <div>
                <div style={{ fontSize:'11px', color:'#9ca3af', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'3px' }}>User</div>
                <div style={{ fontSize:'13.5px', color:'#111827', fontWeight:600 }}>{log.user || 'System'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ width:'34px', height:'34px', borderRadius:'9px', background:'#fdf4ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Tag size={15} color="#a855f7" />
              </div>
              <div>
                <div style={{ fontSize:'11px', color:'#9ca3af', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Action</div>
                <span style={{ display:'inline-block', background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:'7px', padding:'3px 12px', fontSize:'12.5px', fontWeight:700 }}>
                  {log.type}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ width:'34px', height:'34px', borderRadius:'9px', background:'#fff7ed', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <FileText size={15} color="#f97316" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize:'11px', color:'#9ca3af', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Details</div>
                <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.6', background:'#f9fafb', borderRadius:'9px', padding:'11px 14px', border:'1px solid #e5e7eb' }}>
                  {log.details || 'No details available'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding:'14px 22px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 24px', borderRadius:'9px', background:'#111827', color:'#fff', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:700 }}>
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes auditFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes auditSlideUp { from { transform:translateY(14px);opacity:0 } to { transform:translateY(0);opacity:1 } }
      `}</style>
    </div>
  );
};

/* ─── Main component ──────────────────────────────────────── */
const AuditLogs = () => {
  const [searchTerm,     setSearchTerm]     = useState('');
  const [selectedAction, setSelectedAction] = useState('All Actions');
  const [selectedDate,   setSelectedDate]   = useState('');
  const [auditData,      setAuditData]      = useState([]);
  const [filteredData,   setFilteredData]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [actionTypes,    setActionTypes]    = useState(['All Actions']);
  const [, setRefreshing] = useState(false);
  const [selectedLog,    setSelectedLog]    = useState(null);

  // ── pagination ───────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── sorting ──────────────────────────────────────────────
  const [sortField, setSortField] = useState('');
  const [sortDir,   setSortDir]   = useState('asc');

  const BACKEND_URL = getBackendUrl();

  useEffect(() => { fetchAuditLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'#ffffff60', marginLeft:'4px', fontSize:'11px', cursor:'pointer' }}>⇅</span>;
    return <span style={{ marginLeft:'4px', fontSize:'11px', cursor:'pointer' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const fetchAuditLogs = async (isManualRefresh = false) => {
    try {
      isManualRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/dashboard/recent-activity`, {
        method: 'GET', credentials: 'include', mode: 'cors',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });

      if (response.status === 401 || response.status === 403) {
        setError('Access denied. Please log in again.');
        setAuditData([]); setFilteredData([]); return;
      }
      if (!response.ok) {
        setError(`Server error: ${response.status}`);
        setAuditData([]); setFilteredData([]); return;
      }

      const text = await response.text();
      if (!text?.trim()) { setAuditData([]); setFilteredData([]); return; }

      let activities;
      try { activities = JSON.parse(text); }
      catch { setAuditData([]); setFilteredData([]); return; }

      const list = Array.isArray(activities) ? activities : (activities?.activities || []);

      const valid = list
        .filter(a => a && (a.type || a.activity_type || a.activity_title))
        .map(a => ({
          id:            a.id || `temp-${Math.random().toString(36).slice(2)}`,
          type:          a.type || a.activity_title || a.activity_type || 'Activity',
          user:          a.user || a.user_name || 'System',
          time:          a.time || a.created_at || new Date().toISOString(),
          details:       a.details || 'No details available',
          activity_type: a.activity_type || 'unknown',
          created_at:    a.created_at || a.time || new Date().toISOString()
        }));

      setAuditData(valid);
      setActionTypes(['All Actions', ...new Set(valid.map(l => l.type))]);
      setCurrentPage(1);
    } catch (err) {
      setError(`Failed to load: ${err.message}`);
      setAuditData([]); setFilteredData([]);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  // reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedAction, selectedDate]);

  /* Unified filter + sort */
  useEffect(() => {
    let f = [...auditData];

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      f = f.filter(l => l.user?.toLowerCase().includes(s) || l.type?.toLowerCase().includes(s) || l.details?.toLowerCase().includes(s));
    }
    if (selectedAction !== 'All Actions') f = f.filter(l => l.type === selectedAction);
    if (selectedDate) {
      const d = new Date(selectedDate);
      f = f.filter(l => {
        try { return new Date(l.created_at || l.timestamp).toDateString() === d.toDateString(); }
        catch { return false; }
      });
    }

    if (sortField) {
      f.sort((a, b) => {
        if (sortField === 'sno') {
          const indexA = auditData.indexOf(a);
          const indexB = auditData.indexOf(b);
          return sortDir === 'asc' ? indexA - indexB : indexB - indexA;
        }
        let aVal = '', bVal = '';
        if (sortField === 'timestamp') { aVal = a.created_at || a.time || ''; bVal = b.created_at || b.time || ''; }
        if (sortField === 'user')      { aVal = a.user || ''; bVal = b.user || ''; }
        if (sortField === 'type')      { aVal = a.type || ''; bVal = b.type || ''; }
        if (sortField === 'details')   { aVal = a.details || ''; bVal = b.details || ''; }
        if (sortField === 'status')    { aVal = a.activity_type || ''; bVal = b.activity_type || ''; }
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }

    setFilteredData(f);
  }, [searchTerm, selectedAction, selectedDate, auditData, sortField, sortDir]);

  // ── pagination calculations ───────────────────────────────
  const totalRecords = filteredData.length;
  const totalPages   = Math.max(1, Math.ceil(totalRecords / ROWS_PER_PAGE));
  const safePage     = Math.min(currentPage, totalPages);
  const startIndex   = (safePage - 1) * ROWS_PER_PAGE;
  const endIndex     = Math.min(startIndex + ROWS_PER_PAGE, totalRecords);
  const paginated    = filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const paginationStyles = {
    wrapper: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px', borderTop: '1px solid #e5e7eb',
      flexWrap: 'wrap', gap: '12px',
    },
    info: { fontSize: '13px', color: '#6b7280' },
    controls: { display: 'flex', alignItems: 'center', gap: '4px' },
    btn: (active, disabled) => ({
      minWidth: '32px', height: '32px', padding: '0 8px',
      border: active ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
      borderRadius: '6px',
      background: active ? '#3b82f6' : disabled ? '#f9fafb' : '#fff',
      color: active ? '#fff' : disabled ? '#d1d5db' : '#374151',
      fontSize: '13px', cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: active ? '500' : '400', transition: 'all 0.15s',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }),
    ellipsis: {
      minWidth: '32px', height: '32px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', color: '#9ca3af',
    },
  };

  /* export — uses full filteredData, not just current page */
 const handleExport = () => {
  if (!filteredData.length) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Nothing to export</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">No logs match the current filters.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
    return;
  }

  const exportable = filteredData.filter(l => !['error', 'info', 'placeholder'].includes(l.activity_type));

  if (!exportable.length) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">No valid logs</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">No valid logs available to export.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
    return;
  }

  try {
    const csv = [
      ['S.No', 'Timestamp', 'User', 'Action', 'Details', 'Status'].join(','),
      ...exportable.map((l, index) => [
        index + 1,
        formatTimestamp(l.created_at || l.timestamp),
        `"${l.user}"`, `"${l.type}"`,
        `"${(l.details || 'N/A').replace(/"/g, '""')}"`,
        'Success'
      ].join(','))
    ].join('\n');

    const [year, month, day] = new Date().toISOString().split('T')[0].split('-');
    const filename = `audit_logs_${month}-${day}-${year}.csv`;
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
      download: filename
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);

    // ✅ Success
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Export Successful</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Audit logs exported as <strong>${filename}</strong></p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });

  } catch {
    // ✅ Error
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Export Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Something went wrong while exporting. Please try again.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  }
};

  /* ── loading ───────────────────────────────────────────── */
  if (loading) return (
    <div className="Admin-auditlogs-navba">
      <div className="Admin-audit-nav"><Navbar /></div>
      <div className="Admin-auditlogs-container">
        <TopBar />
        <div className="audit-body">
          <div className="audit-state-box">
            <Loader2 size={40} className="audit-spinner" style={{ color:'#3b82f6' }} />
            <p>Loading audit logs…</p>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── error ─────────────────────────────────────────────── */
  if (error && !auditData.length) return (
    <div className="Admin-auditlogs-navba">
      <div className="Admin-audit-nav"><Navbar /></div>
      <div className="Admin-auditlogs-container">
        <header className="audit-topbar"></header>
        <div className="audit-body">
          <div className="audit-state-box">
            <AlertCircle size={44} style={{ color:'#ef4444' }} />
            <p>Error Loading Logs</p>
            <small>{error}</small>
            <button className="audit-retry-btn" onClick={() => fetchAuditLogs(false)}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── main render ───────────────────────────────────────── */
  return (
    <div className="Admin-auditlogs-navba" style={{ overflow:'hidden' }}>
      <div className="Admin-audit-nav"><Navbar /></div>

      {selectedLog && (
        <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}

      <div className="Admin-auditlogs-container">
        <TopBar />

        <div className="audit-body">
          <div className="audit-page-header">
            <div>
              <h1>Audit Logs</h1>
              <p>Track all system activities ({totalRecords} log{totalRecords !== 1 ? 's' : ''})</p>
            </div>
          </div>

          <div className="audit-filter-card">
            <div className="audit-search-wrap">
              <svg width="13" height="13" fill="none" viewBox="0 0 20 20">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search by user, action, details…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            <select className="audit-select" value={selectedAction} onChange={e => setSelectedAction(e.target.value)}>
              {actionTypes.map((a, i) => <option key={i} value={a}>{a}</option>)}
            </select>

            <input
              type="date"
              className="audit-date-input"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max="9999-12-31"
            />

            <div className="audit-filter-spacer" />

            <button className="audit-export-btn" onClick={handleExport} disabled={filteredData.length === 0}>
              <Download size={14} />
              Export Logs
            </button>
          </div>

          <div className="audit-table-section">
            <div className="audit-table-title">
              <span>System Activity Log</span>
              <span className="audit-count-badge">
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {totalRecords} records
              </span>
            </div>

            <div className="audit-table-wrapper">
              {totalRecords === 0 ? (
                <div className="audit-state-box">
                  <svg width="48" height="48" fill="none" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <p>No logs found</p>
                  <small>
                    {searchTerm || selectedAction !== 'All Actions' || selectedDate
                      ? 'Try adjusting your filters'
                      : 'Start using the system to see audit logs'}
                  </small>
                </div>
              ) : (
                <>
                  <table className="audit-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('sno')} style={{ cursor:'pointer', userSelect:'none' }}>
                          S.No <SortIcon field="sno" />
                        </th>
                        <th onClick={() => handleSort('timestamp')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Timestamp <SortIcon field="timestamp" />
                        </th>
                        <th onClick={() => handleSort('user')} style={{ cursor:'pointer', userSelect:'none' }}>
                          User <SortIcon field="user" />
                        </th>
                        <th onClick={() => handleSort('type')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Action <SortIcon field="type" />
                        </th>
                        <th onClick={() => handleSort('details')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Details <SortIcon field="details" />
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Status <SortIcon field="status" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((log, pageIndex) => {
                        // const globalIndex = startIndex + pageIndex;
                        // const displayIndex = sortField === 'sno' && sortDir === 'desc'
                        //   ? totalRecords - globalIndex
                        //   : globalIndex + 1;
                        
                        const displayIndex = auditData.indexOf(log) + 1;
                        return (
                          <tr
                            key={log.id || pageIndex}
                            onClick={() => setSelectedLog(log)}
                            className="audit-row-clickable"
                          >
                            <td style={{ fontWeight:'500' }}>{displayIndex}</td>
                            <td>
                              <span className="audit-ts">{formatTimestamp(log.created_at || log.timestamp)}</span>
                            </td>
                            <td>
                              <span className="audit-user">{log.user}</span>
                            </td>
                            <td>
                              <span className="audit-action-badge">{log.type}</span>
                            </td>
                            <td>
                              <span className="audit-details">{log.details}</span>
                            </td>
                            <td>
                              {log.activity_type === 'error' ? (
                                <span className="audit-status-error">
                                  <svg width="10" height="10" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                  Error
                                </span>
                              ) : (
                                <span className="audit-status-success">
                                  <svg width="10" height="10" fill="none" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  Success
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Pagination Bar ── */}
                  {totalPages > 1 && (
                    <div style={paginationStyles.wrapper}>
                      <span style={paginationStyles.info}>
                        Showing {startIndex + 1}–{endIndex} of {totalRecords} logs
                      </span>
                      <div style={paginationStyles.controls}>
                        <button
                          style={paginationStyles.btn(false, safePage === 1)}
                          onClick={() => goToPage(safePage - 1)}
                          disabled={safePage === 1}
                          title="Previous page"
                        >
                          ‹
                        </button>
                        {getPageNumbers().map((page, idx) =>
                          page === '...'
                            ? <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                            : (
                              <button
                                key={page}
                                style={paginationStyles.btn(page === safePage, false)}
                                onClick={() => goToPage(page)}
                              >
                                {page}
                              </button>
                            )
                        )}
                        <button
                          style={paginationStyles.btn(false, safePage === totalPages)}
                          onClick={() => goToPage(safePage + 1)}
                          disabled={safePage === totalPages}
                          title="Next page"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}

                  {totalPages === 1 && totalRecords > 0 && (
                    <div style={{ ...paginationStyles.wrapper, justifyContent: 'flex-end' }}>
                      <span style={paginationStyles.info}>
                        {totalRecords} log{totalRecords !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
